import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { usePermission } from "@/lib/usePermission";
import { RoleBadge } from "@/components/RoleBadge";
import { Button } from "@/components/ui/button";
import { LogOut, ChevronDown, FileText, ShieldCheck, KeyRound, UserCircle2, Users2, Settings, Inbox, History, BookOpen, Send, CreditCard, Repeat, LayoutDashboard, Shield } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
    DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { NotificationBell } from "@/components/NotificationBell";
import { OPEN_DASHBOARD_PREFS_EVENT } from "@/components/DashboardPreferencesDialog";

export function TopNav() {
    const { user, logout } = useAuth();
    const { tenant } = useTenant();
    const { ready: permReady, hasPermission } = usePermission();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    if (!user) return null;

    const dashboardHref = user.role === "customer" ? "/customer" : user.role === "manager" ? "/manager" : "/admin";
    const displayName = tenant?.display_name || "RentFlow";
    const logoUrl = tenant?.logo_url || "/favicon.png";

    return (
        <header
            data-testid="top-nav"
            className="sticky top-0 z-40 w-full bg-white/80 backdrop-blur-md border-b border-slate-200"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
                <Link
                    to={dashboardHref}
                    data-testid="nav-logo"
                    className="flex items-center gap-2.5 text-slate-900"
                >
                    <img
                        src={logoUrl}
                        alt={displayName}
                        data-testid="nav-logo-image"
                        className="h-8 w-auto max-w-[140px] object-contain rounded-md"
                    />
                    <div className="leading-tight">
                        <div
                            data-testid="nav-tenant-name"
                            className="font-display font-bold text-lg tracking-tight"
                        >
                            {displayName}
                        </div>
                        <div className="hidden sm:block text-[10px] uppercase tracking-[0.18em] text-slate-500 -mt-0.5">
                            Customer portal
                        </div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    <NavLink
                        to={dashboardHref}
                        data-testid="nav-dashboard"
                        className={({ isActive }) =>
                            `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                            }`
                        }
                        end
                    >
                        Dashboard
                    </NavLink>
                    {(user.role === "admin" || user.role === "super_admin") && (
                        <>
                            <NavLink
                                to="/admin/invites"
                                data-testid="nav-invites"
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                    }`
                                }
                            >
                                Invites
                            </NavLink>
                            <NavLink
                                to="/admin/submissions"
                                data-testid="nav-submissions"
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                    }`
                                }
                            >
                                Submissions
                            </NavLink>
                            <NavLink
                                to="/admin/customers"
                                data-testid="nav-customers"
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                    }`
                                }
                            >
                                Customers
                            </NavLink>
                            <NavLink
                                to="/admin/rentals"
                                data-testid="nav-rentals"
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                    }`
                                }
                            >
                                Rentals
                            </NavLink>
                            {/* Phase K.7 (2026-08-21) — Statements top-nav link.
                                Widened from super_admin-only (K.1a) to
                                admin + super_admin so admins can jump
                                straight to the global list without
                                picking a customer first. Sits between
                                Rentals and Tolls in the IA. */}
                            <NavLink
                                to="/admin/statements"
                                data-testid="admin-nav-statements"
                                className={({ isActive }) => {
                                    // Highlight for /admin/statements and any subroute (/statements/:id).
                                    const onStatements = typeof window !== "undefined" &&
                                        window.location.pathname.startsWith("/admin/statements");
                                    const active = isActive || onStatements;
                                    return `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        active ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                    }`;
                                }}
                            >
                                Statements
                            </NavLink>
                            {/* Phase TOLLBILL-P5c — Tolls nav points to Bills (primary entity). */}
                            {permReady && hasPermission("Tolls", "View") && (
                                <NavLink
                                    to="/admin/tolls/bills"
                                    data-testid="nav-tolls"
                                    className={({ isActive }) => {
                                        // Also highlight on the legacy transactions subroute (/admin/tolls).
                                        const onTolls = typeof window !== "undefined" && window.location.pathname.startsWith("/admin/tolls");
                                        const active = isActive || onTolls;
                                        return `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                            active ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                        }`;
                                    }}
                                >
                                    Tolls
                                </NavLink>
                            )}
                            {/* Phase O.2 — Templates moved out of the main nav into the user menu (super-admin only). */}
                        </>
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    {/* Phase J.3.2 — notification bell (both customer + admin). */}
                    <NotificationBell role={user.role === "customer" ? "customer" : "admin"} />
                    <UserMenu user={user} handleLogout={handleLogout} />
                </div>
            </div>
        </header>
    );
}


/**
 * Phase O.2 — the standalone top-nav Templates dropdown was moved into
 * the user-menu and is now rendered as a ``DropdownMenuSub`` inside
 * ``UserMenu`` (super-admin only). The previous ``TemplatesNavDropdown``
 * helper was removed to avoid a dead export.
 */


/**
 * Phase G — role-based user menu anchored at the right end of the top nav.
 * Trigger: avatar (initials) + name + role chip + caret on md+; avatar-only
 * below md to save horizontal space. Dropdown items vary by role.
 */
function UserMenu({ user, handleLogout }) {
    const navigate = useNavigate();
    const initials = (user.full_name || user.email || "?")
        .split(/\s+/)
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
    const roleLabel = user.role === "super_admin" ? "Super Admin" : user.role === "admin" ? "Admin" : "Customer";
    const isCustomer = user.role === "customer";
    const isSuper = user.role === "super_admin";

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid="user-menu-trigger"
                    aria-label="User menu"
                    aria-haspopup="menu"
                    className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm hover:border-slate-300 hover:bg-slate-50 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-300">
                    <span
                        data-testid="user-menu-avatar"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-900 text-white text-[11px] font-semibold tracking-wide">
                        {initials}
                    </span>
                    <span className="hidden md:flex flex-col items-start leading-tight">
                        <span data-testid="nav-user-name" className="text-sm font-medium text-slate-900">{user.full_name}</span>
                        <span className="text-[11px] text-slate-500">{roleLabel}</span>
                    </span>
                    <ChevronDown size={14} className="hidden md:inline opacity-70 -mr-0.5" aria-hidden="true" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[240px]" data-testid="user-menu-content">
                <DropdownMenuLabel className="font-normal pb-2">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-slate-900" data-testid="user-menu-fullname">{user.full_name}</span>
                        <span className="text-xs text-slate-500" data-testid="user-menu-email">{user.email}</span>
                        <span
                            data-testid="user-menu-role-chip"
                            className={
                                "mt-1 inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset " +
                                (isSuper
                                    ? "bg-blue-50 text-blue-900 ring-blue-200"
                                    : user.role === "admin"
                                        ? "bg-slate-100 text-slate-800 ring-slate-200"
                                        : "bg-emerald-50 text-emerald-900 ring-emerald-200")
                            }>
                            {roleLabel}
                        </span>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {isCustomer && (
                    <>
                        <DropdownMenuItem
                            data-testid="user-menu-my-profile"
                            onSelect={() => navigate("/customer/profile")}
                            className="gap-2">
                            <UserCircle2 size={14} className="text-slate-500" />
                            My Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            data-testid="user-menu-payment-methods"
                            onSelect={() => navigate("/customer/payment-methods")}
                            className="gap-2">
                            <CreditCard size={14} className="text-slate-500" />
                            Payment Methods
                        </DropdownMenuItem>
                    </>
                )}

                <DropdownMenuItem
                    data-testid="user-menu-change-password"
                    onSelect={() => navigate("/change-password")}
                    className="gap-2">
                    <KeyRound size={14} className="text-slate-500" />
                    Change Password
                </DropdownMenuItem>

                {!isCustomer && (
                    <DropdownMenuItem
                        data-testid="user-menu-dashboard-prefs"
                        onSelect={(ev) => {
                            // Defer so the dropdown's own onSelect can finish
                            // closing the menu before the dialog opens —
                            // Radix moves focus on close, and a same-tick
                            // open will fight that animation.
                            ev?.preventDefault?.();
                            setTimeout(() => {
                                window.dispatchEvent(new CustomEvent(OPEN_DASHBOARD_PREFS_EVENT));
                            }, 0);
                        }}
                        className="gap-2">
                        <LayoutDashboard size={14} className="text-slate-500" />
                        Dashboard preferences
                    </DropdownMenuItem>
                )}

                {isSuper && (
                    <>
                        <DropdownMenuItem
                            data-testid="user-menu-manage-admins"
                            onSelect={() => navigate("/admin/admins")}
                            className="gap-2">
                            <Users2 size={14} className="text-slate-500" />
                            Manage Admins
                        </DropdownMenuItem>
                        {/* Phase O.2 — Templates submenu (super_admin only).
                            Moved out of the main top-nav into the user
                            menu so the global nav stays focused on
                            day-to-day workflows. Children preserved:
                            Contract Templates, ACH Templates, COI
                            Requirement Templates, Invoice Templates. */}
                        <DropdownMenuSub>
                            <DropdownMenuSubTrigger
                                data-testid="user-menu-templates-trigger"
                                className="gap-2"
                            >
                                <FileText size={14} className="text-slate-500" />
                                Templates
                            </DropdownMenuSubTrigger>
                            <DropdownMenuPortal>
                                <DropdownMenuSubContent
                                    className="min-w-[220px]"
                                    data-testid="user-menu-templates-submenu"
                                >
                                    <DropdownMenuItem
                                        data-testid="user-menu-templates-contracts"
                                        onSelect={() => navigate("/admin/templates/contracts")}
                                        className="gap-2"
                                    >
                                        <FileText size={14} className="text-slate-500" />
                                        Contract Templates
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        data-testid="user-menu-templates-ach"
                                        onSelect={() => navigate("/admin/templates/ach")}
                                        className="gap-2"
                                    >
                                        <FileText size={14} className="text-slate-500" />
                                        ACH Templates
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        data-testid="user-menu-templates-coi"
                                        onSelect={() => navigate("/admin/coi-templates")}
                                        className="gap-2"
                                    >
                                        <ShieldCheck size={14} className="text-slate-500" />
                                        COI Requirement Templates
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        data-testid="user-menu-templates-invoices"
                                        onSelect={() => navigate("/admin/templates/invoices")}
                                        className="gap-2"
                                    >
                                        <FileText size={14} className="text-slate-500" />
                                        Invoice Templates
                                    </DropdownMenuItem>
                                </DropdownMenuSubContent>
                            </DropdownMenuPortal>
                        </DropdownMenuSub>
                        <DropdownMenuItem
                            data-testid="user-menu-mock-emails"
                            onSelect={() => navigate("/admin/mock-emails")}
                            className="gap-2">
                            <Inbox size={14} className="text-slate-500" />
                            Mock Emails
                        </DropdownMenuItem>
                        {/* Phase I — Email send log (super-admin only). */}
                        <DropdownMenuItem
                            data-testid="user-menu-email-log"
                            onSelect={() => navigate("/admin/email-log")}
                            className="gap-2">
                            <Send size={14} className="text-slate-500" />
                            Email Send Log
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            data-testid="user-menu-impersonation-log"
                            onSelect={() => navigate("/admin/impersonation-audit")}
                            className="gap-2">
                            <History size={14} className="text-slate-500" />
                            Impersonation Log
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            data-testid="user-menu-system-settings"
                            onSelect={() => navigate("/admin/settings")}
                            className="gap-2">
                            <Settings size={14} className="text-slate-500" />
                            System Settings
                        </DropdownMenuItem>
                        {/* Task 13 — read-only permissions matrix (any admin/manager). */}
                        <DropdownMenuItem
                            data-testid="user-menu-permissions"
                            onSelect={() => navigate("/admin/permissions")}
                            className="gap-2">
                            <Shield size={14} className="text-slate-500" />
                            Permissions
                        </DropdownMenuItem>
                        {/* Phase H.13b — Clause Library (super_admin only). */}
                        <DropdownMenuItem
                            data-testid="user-menu-clause-library"
                            onSelect={() => navigate("/admin/clauses")}
                            className="gap-2">
                            <BookOpen size={14} className="text-slate-500" />
                            Clause Library
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            data-testid="user-menu-auto-charge"
                            onSelect={() => navigate("/admin/auto-charge-attempts")}
                            className="gap-2">
                            <Repeat size={14} className="text-slate-500" />
                            Auto-charge Attempts
                        </DropdownMenuItem>
                    </>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem
                    data-testid="user-menu-sign-out"
                    onSelect={handleLogout}
                    className="gap-2 text-red-700 focus:text-red-800 focus:bg-red-50">
                    <LogOut size={14} />
                    Sign Out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
